import type { CalendarAvailability, CalendarSlot } from './calendar.types';

export function toDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatSpots(
  slot: CalendarSlot
): { text: string; className: string } | null {
  if (slot.availableSpots === undefined) return null;
  // A capacity-1 class reads "1 spot left" for its entire life until booked, so the
  // label carries no scarcity signal, only noise.
  if (slot.totalSpots === 1) return null;
  if (isSlotFull(slot))
    return { text: 'Fully booked', className: 'text-gray-400' };
  if (slot.availableSpots === 1)
    return { text: '1 spot left', className: 'text-error-500' };
  return {
    text: `${slot.availableSpots} spots`,
    className: 'text-gray-500',
  };
}

// The slot list's rule, read by every surface that prints a time's spots.
// spotsUrgencyOnly shows "1 spot left" whatever showSpots says and keeps the
// larger counts hidden.
export function visibleSpots(
  slot: CalendarSlot,
  {
    showSpots,
    spotsUrgencyOnly,
  }: { showSpots: boolean; spotsUrgencyOnly: boolean }
): { text: string; className: string } | null {
  if (!showSpots && !spotsUrgencyOnly) return null;
  if (
    spotsUrgencyOnly &&
    !isSlotFull(slot) &&
    slot.availableSpots !== undefined &&
    slot.availableSpots > 1
  )
    return null;
  return formatSpots(slot);
}

export function getFirstAvailableDate(
  availability: CalendarAvailability
): Date | null {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayKey = toDateKey(today);

  const firstKey = Object.keys(availability)
    .filter((key) => availability[key].length > 0 && key >= todayKey)
    .sort()[0];

  if (!firstKey) return null;

  const [y, m, d] = firstKey.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function getFirstClassMonth(
  availability: CalendarAvailability
): Date | null {
  const first = getFirstAvailableDate(availability);
  if (!first) return null;
  const today = new Date();
  if (
    first.getFullYear() === today.getFullYear() &&
    first.getMonth() === today.getMonth()
  )
    return null;
  return new Date(first.getFullYear(), first.getMonth(), 1);
}

export function formatMonthYear(date: Date, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    month: 'long',
    year: 'numeric',
  }).format(date);
}

export function isMonthEmpty(
  availability: CalendarAvailability,
  month: Date
): boolean {
  const y = month.getFullYear();
  const m = month.getMonth();
  return !Object.keys(availability).some((key) => {
    if (!availability[key].length) return false;
    const [ky, km] = key.split('-').map(Number);
    return ky === y && km - 1 === m;
  });
}

export function getClosestAvailableDate(
  availability: CalendarAvailability,
  excludeMonth: Date
): Date | null {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayTime = today.getTime();

  const exY = excludeMonth.getFullYear();
  const exM = excludeMonth.getMonth();

  let closest: Date | null = null;
  let minDist = Infinity;

  for (const key of Object.keys(availability)) {
    if (!availability[key].length) continue;
    const [y, m, d] = key.split('-').map(Number);
    if (y === exY && m - 1 === exM) continue;
    const candidate = new Date(y, m - 1, d);
    const dist = Math.abs(candidate.getTime() - todayTime);
    if (dist < minDist) {
      minDist = dist;
      closest = candidate;
    }
  }

  return closest;
}

export function formatSelectedDate(date: Date, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

export function formatBookingSummaryDate(date: Date, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

// The server flag also covers a class minimum per booking, where the last
// spots can read as open by count yet be unbookable.
export function isSlotFull(slot: CalendarSlot): boolean {
  return slot.isFullyBooked ?? slot.availableSpots === 0;
}

export function hasFullSlot(availability: CalendarAvailability): boolean {
  return Object.values(availability).some((slots) => slots.some(isSlotFull));
}

// A slot id repeats every week, so the start time pins the occurrence.
export function findSameSlot(
  slots: CalendarSlot[] | undefined,
  slot: CalendarSlot
): CalendarSlot | undefined {
  return slots?.find((s) => s.id === slot.id && s.startTime === slot.startTime);
}

export interface SlotGroup {
  instructorName: string | null;
  instructorId: string;
  instructorAvatar?: string;
  isSubstitute?: boolean;
  slots: CalendarSlot[];
}

function compareSlotStart(a: CalendarSlot, b: CalendarSlot): number {
  return new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
}

// One teacher can come in several groups on a day (her own class times and a
// cover she teaches sit on different schedules), so a joined group is put back
// in time order. Teachers keep the order they came in.
export function groupSlotsByInstructor(
  slots: CalendarSlot[],
  showInstructor: boolean
): SlotGroup[] {
  const hasInstructors = showInstructor && slots.some((s) => s.instructorName);
  if (!hasInstructors) {
    return [
      {
        instructorName: null,
        instructorId: '__default__',
        slots: [...slots].sort(compareSlotStart),
      },
    ];
  }
  const map = new Map<string, SlotGroup>();
  for (const slot of slots) {
    const key = slot.instructorId ?? slot.instructorName ?? '__ungrouped__';
    const existing = map.get(key);
    if (existing) {
      existing.slots.push(slot);
    } else {
      map.set(key, {
        instructorName: slot.instructorName ?? null,
        instructorId: key,
        instructorAvatar: slot.instructorAvatar,
        isSubstitute: slot.isSubstitute,
        slots: [slot],
      });
    }
  }
  const groups = Array.from(map.values());
  for (const group of groups) group.slots.sort(compareSlotStart);
  return groups;
}
